import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, FileUp } from "lucide-react";
import Seo from "../components/Seo";
import Reveal, { Eyebrow, EASE } from "../components/Reveal";
import ContactSection from "../components/ContactSection";
import { SERVICES } from "../constants/content";
import { useSite } from "../context/SiteContext";

const SERVICE_IMG_KEYS = { renovations: "renovation", extensions: "deck", "new-builds": "newbuild" };

export default function ServicePage({ slug }) {
  const { images } = useSite();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return null;
  const others = SERVICES.filter((x) => x.slug !== slug);
  const heroImg = images[SERVICE_IMG_KEYS[s.slug]] || s.image;

  return (
    <>
      <Seo title={s.seoTitle} description={s.seoDesc} />
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-ink overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="w-full h-full object-cover opacity-30 img-duotone" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold">
            {s.num} — Our Services
          </motion.p>
          <h1 className="mt-4 font-display font-bold text-4xl md:text-6xl tracking-tight text-white display-expanded leading-[1.0] max-w-3xl overflow-hidden">
            <motion.span className="block" initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>
              {s.title}
            </motion.span>
          </h1>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5, ease: EASE }} className="mt-5 text-white/75 text-lg max-w-xl">
            {s.tagline}
          </motion.p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            {s.intro.map((p, i) => (
              <p key={i} className={`${i === 0 ? "text-lg md:text-xl text-ink/85 font-medium" : "mt-5 text-slate2"} leading-relaxed`}>{p}</p>
            ))}
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to={`/contact?type=${encodeURIComponent(s.enquiryType)}`} data-testid={`service-page-cta-${s.slug}`} className="inline-flex items-center gap-2.5 bg-gold hover:bg-golddark text-white font-mono text-[11px] tracking-[0.18em] uppercase px-6 py-4 transition-colors">
                {s.cta} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact#upload" data-testid={`service-page-plans-${s.slug}`} className="inline-flex items-center gap-2.5 border border-ink px-6 py-4 font-mono text-[11px] tracking-[0.18em] uppercase text-ink hover:bg-ink hover:text-white transition-colors">
                <FileUp className="w-4 h-4" /> Send Plans
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="bg-paper border border-line p-7 md:p-9">
              <Eyebrow>What This Covers</Eyebrow>
              <ul className="mt-6 space-y-4">
                {s.includes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-ink/85">
                    <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-paper border-t border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Eyebrow>Also From Razor Build</Eyebrow>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {others.map((o) => (
              <Link key={o.slug} to={`/${o.slug}`} data-testid={`other-service-${o.slug}`} className="group relative overflow-hidden bg-ink min-h-[200px] flex items-end">
                <img src={images[SERVICE_IMG_KEYS[o.slug]] || o.image} alt={o.imageAlt} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-40 img-duotone group-hover:opacity-55 group-hover:scale-[1.03] transition-all duration-700" />
                <div className="relative p-7 flex items-center justify-between w-full">
                  <div>
                    <span className="font-mono text-[10px] tracking-[0.25em] text-gold">{o.num}</span>
                    <h3 className="font-display font-bold text-2xl text-white uppercase display-expanded">{o.title}</h3>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gold group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactSection defaultType={s.enquiryType} />
    </>
  );
}
